import { holdsAnywhere } from '@carii/casl-abilities';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { useAbility } from '../../context/AbilityContext.js';
import { api } from '../../services/api.js';
import { mytClock, mytDayLabel } from '../../utils/myt.js';
import { ChangeSupplierDialog } from './ChangeSupplierDialog.js';

/**
 * 360 (#1147): the car's supplier on its edit page (FR-001), the administrator's way to change it (FR-002), and the
 * car's history of suppliers (FR-019). Its own card, outside the car form: the general save never changes the supplier.
 * "Change supplier" is offered only to staff who manage cars PLATFORM-WIDE (`holdsAnywhere`): a supplier's own
 * `update Car` is scoped to its organisation and passes a type-level check.
 */
interface SupplierChangeItem {
  id: string;
  from: { id: string; name: string };
  to: { id: string; name: string };
  by: { id: string; name: string };
  at: string;
  moved_rentals: number;
}

/** 369/377: one change of the car's hosts (`GET /cars/:id/host-changes`); an empty side is "No host". */
interface HostChangeItem {
  id: string;
  from_hosts: Array<{ id: string; name: string }>;
  to_hosts: Array<{ id: string; name: string }>;
  by_name: string;
  at: string;
  cause: 'assign' | 'supplier_change';
  moved_rentals: number;
}

export function CarSupplierCard({
  carId,
  supplier,
  onMoved,
}: {
  carId: string;
  supplier: { id: string; company_name: string } | null | undefined;
  onMoved: () => void;
}) {
  const ability = useAbility();
  const mayMove = holdsAnywhere(ability as never, 'manage', 'Car');
  const [open, setOpen] = useState(false);
  const history = useQuery({
    queryKey: ['car-supplier-changes', carId],
    queryFn: () => api.get<{ items: SupplierChangeItem[] }>(`/cars/${carId}/supplier-changes`),
  });
  const items = history.data?.items ?? [];
  // 369: keyed under the car's own key, so saving the car (a host change) refreshes it.
  const hostHistory = useQuery({
    queryKey: ['car', carId, 'host-changes'],
    queryFn: () => api.get<{ items: HostChangeItem[] }>(`/cars/${carId}/host-changes`),
  });
  const hostItems = hostHistory.data?.items ?? [];
  const hostNames = (hs: Array<{ name: string }>) =>
    hs.length ? hs.map((h) => h.name || 'Former host').join(', ') : 'No host';

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="mb-1 text-sm font-semibold text-ink">Supplier</h2>
          <p data-testid="car-supplier-name" className="text-sm text-ink">
            {supplier?.company_name || '—'}
          </p>
        </div>
        {mayMove && supplier && (
          <Button type="button" variant="secondary" onClick={() => setOpen(true)}>
            Change supplier
          </Button>
        )}
      </div>
      {items.length > 0 && (
        <div className="mt-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-steel">
            Supplier history
          </h3>
          <ul data-testid="supplier-history" className="space-y-1 text-sm text-ink">
            {items.map((i) => (
              <li key={i.id}>
                {i.from.name} → {i.to.name}, {mytDayLabel(i.at)} {mytClock(i.at)}
                {i.by.name ? `, by ${i.by.name}` : ''} ({i.moved_rentals}{' '}
                {i.moved_rentals === 1 ? 'rental' : 'rentals'} moved)
              </li>
            ))}
          </ul>
        </div>
      )}
      {hostItems.length > 0 && (
        <div className="mt-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-steel">
            Host history
          </h3>
          <ul data-testid="host-history" className="space-y-1 text-sm text-ink">
            {hostItems.map((i) => (
              <li key={i.id}>
                {hostNames(i.from_hosts)} → {hostNames(i.to_hosts)}, {mytDayLabel(i.at)}{' '}
                {mytClock(i.at)}
                {i.by_name ? `, by ${i.by_name}` : ''}
                {i.cause === 'supplier_change' ? ', with the supplier change' : ''}
                {/* 369's records moved bookings' credit; 377's never do (money is the supplier's). */}
                {i.moved_rentals > 0
                  ? ` (${i.moved_rentals} ${i.moved_rentals === 1 ? 'rental' : 'rentals'} moved)`
                  : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
      {mayMove && supplier && (
        <ChangeSupplierDialog
          open={open}
          onClose={() => setOpen(false)}
          carId={carId}
          currentSupplierId={supplier.id}
          onMoved={() => {
            history.refetch();
            hostHistory.refetch();
            onMoved();
          }}
        />
      )}
    </Card>
  );
}
